import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import RepairRequestForm from '../components/Request/RepairRequestForm';
import { serviceProviderService } from '../services/serviceProviderService';
import type { ServiceProvider } from '../types/firestore';
import {
  canCreateRepairRequestForServiceProvider,
  isOwnServiceProviderTarget,
  isServiceAdmin,
} from '../utils/accessControl';
import { Alert, Button, Spinner } from '../components/common/UIComponents'; // Import common components
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { resolveServiceAvailabilityStatus } from '../utils/businessHours';
import { getServiceProviderProfilePath } from '../utils/serviceProviderProfilePath';

const NewRepairRequestPage: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const { currentUser, userProfile, claims, loading: loadingProfile } = useAuth();
  const [serviceProvider, setServiceProvider] = useState<ServiceProvider | null>(null);
  const [demoSubmitMode, setDemoSubmitMode] = useState(false);
  const [loadingName, setLoadingName] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null); // Add error state
  const targetServiceProviderId = serviceId || null;
  const isOwnServiceTarget = isOwnServiceProviderTarget(
    currentUser?.uid || null,
    targetServiceProviderId
  );
  const isServiceActor = isServiceAdmin(userProfile, claims);
  const canCreateForThisProvider = canCreateRepairRequestForServiceProvider(
    userProfile,
    claims,
    currentUser?.uid || null,
    targetServiceProviderId
  );
  const availabilityStatus = serviceProvider
    ? resolveServiceAvailabilityStatus(serviceProvider)
    : null;

  // Fetch service name
  useEffect(() => {
    if (!targetServiceProviderId) {
      setError('Brak wybranego serwisu. Przejdź do Odkrywaj i wybierz serwis docelowy.');
      setLoadingName(false);
      return;
    }

    if (!loadingProfile && !canCreateForThisProvider) {
      if (isServiceActor && isOwnServiceTarget) {
        setError(
          'Nie możesz utworzyć zlecenia do własnego serwisu. Wybierz inny serwis w Odkrywaj.'
        );
        setLoadingName(false);
        return;
      }
      setError(
        'Nie masz uprawnień do utworzenia zgłoszenia dla tego serwisu. Zgłoszenia wymaga aktywnego konta klienta, administratora albo aktywnego serwisu.'
      );
      setLoadingName(false);
      return;
    }

    if (canCreateForThisProvider) {
      setLoadingName(true);
      setError(null); // Clear previous errors
      setServiceProvider(null);
      setDemoSubmitMode(false);

      const loadCreateRequestProfile = async () => {
        try {
          const discoverableProfile =
            await serviceProviderService.getPublicDiscoverableServiceProviderProfile(
              targetServiceProviderId
            );
          if (!discoverableProfile) {
            setError('Ten serwis jest obecnie niedostępny.');
            return;
          }

          if (discoverableProfile.isTestData === true) {
            setServiceProvider(discoverableProfile);
            setDemoSubmitMode(true);
            return;
          }

          const operationalProfile =
            await serviceProviderService.getPublicOperationalServiceProviderProfile(
              targetServiceProviderId
            );
          if (!operationalProfile) {
            setError('Ten serwis jest obecnie niedostępny.');
            return;
          }

          setServiceProvider(operationalProfile);
        } catch (err) {
          console.error('Błąd pobierania nazwy serwisu:', err);
          setError('Nie udało się pobrać informacji o serwisie.');
        } finally {
          setLoadingName(false);
        }
      };

      void loadCreateRequestProfile();
    }
  }, [
    canCreateForThisProvider,
    isOwnServiceTarget,
    isServiceActor,
    loadingProfile,
    targetServiceProviderId,
  ]);

  if (loadingProfile) {
    return (
      <div className="px-4 py-6 sm:py-8 text-center">
        <Spinner size="md" />
      </div>
    );
  }

  if (error) {
    const shouldRedirectToDiscover = isOwnServiceTarget || !targetServiceProviderId;
    return (
      <div className="px-4 py-6 sm:py-8 text-center">
        <p className="mx-auto max-w-md text-status-error-500">{error}</p>
        <Button
          variant="primary"
          size="sm"
          className="mt-4"
          onClick={() => navigate(shouldRedirectToDiscover ? '/app/discover' : '/app/my-requests')}
        >
          {shouldRedirectToDiscover ? 'Przejdź do Odkrywaj' : 'Wróć do moich zleceń'}
        </Button>
      </div>
    );
  }

  return (
    <div className="rs-form-page rs-page-gutter py-3 sm:py-5 lg:py-7">
      <div className="rs-form-shell mx-auto" data-testid="create-request-form-shell">
        <header className="rs-form-header">
          <button
            type="button"
            onClick={() => {
              if (!targetServiceProviderId) {
                navigate('/app/discover');
                return;
              }
              navigate(getServiceProviderProfilePath(targetServiceProviderId));
            }}
            className="rs-form-back-action focus-outline"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            <span>{!targetServiceProviderId ? 'Wróć do Odkrywaj' : 'Wróć do profilu serwisu'}</span>
          </button>

          <div className="min-w-0">
            <p className="rs-form-eyebrow">Zlecenia / nowe zgłoszenie</p>
            <h1 className="rs-form-title">Nowe zgłoszenie naprawy</h1>
            <p className="rs-form-lede">
              Opisz sprzęt i problem, a potem wybierz sposób realizacji.
            </p>
          </div>
        </header>

        {loadingName && (
          <div className="rs-form-notice flex items-center gap-2 text-sm text-primary-light-300">
            <Spinner size="sm" />
            <span>Przygotowujemy dane serwisu…</span>
          </div>
        )}

        {serviceProvider ? (
          <>
            {availabilityStatus?.state === 'temporarily_unavailable' && (
              <Alert variant="warning" className="rs-form-notice !p-3">
                <span className="font-semibold">{availabilityStatus.summaryText}</span>
                {availabilityStatus.description && (
                  <span className="mt-1 block">{availabilityStatus.description}</span>
                )}
              </Alert>
            )}
            <RepairRequestForm
              serviceProviderId={targetServiceProviderId || ''}
              serviceProvider={serviceProvider}
              demoSubmitMode={demoSubmitMode}
              demoSubmitReason={demoSubmitMode ? 'testServiceProvider' : undefined}
            />
          </>
        ) : loadingName ? null : (
          <p className="rs-form-notice text-sm text-primary-light-300">
            Nie udało się przygotować formularza dla tego serwisu.
          </p>
        )}
      </div>
    </div>
  );
};

export default NewRepairRequestPage;
